import React, { useState, useEffect, useCallback } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import { motion } from 'framer-motion';
import { FaUser, FaEye, FaEyeSlash, FaEnvelope, FaQrcode, FaMobileAlt, FaCheckCircle } from 'react-icons/fa';
import '../../../styles/password-input.css';
import { useDispatch, useSelector } from 'react-redux';
import { loginUser, enable2FA, verify2FA, clear2FA } from '../../../redux/Slices/authSlice';
import { setUserRoles } from '../../../redux/Slices/PermissionsSlice';
import { useNavigate } from 'react-router-dom';

import 'react-toastify/dist/ReactToastify.css';
import { showSuccessToast, showErrorToast } from '../../../shared/utils/toastHelper';
import { getWebFcmToken } from '../../../firebase/webPush';

const AdminLoginPage = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [shake, setShake] = useState(false);
    const [loading, setLoading] = useState(false);
    const [verificationCode, setVerificationCode] = useState('');
    const [showQRModal, setShowQRModal] = useState(false);
    const [showVerifyModal, setShowVerifyModal] = useState(false);
    const [show2FAPromptModal, setShow2FAPromptModal] = useState(false);

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { user, need2FA, twoFAEmail, twoFAData, isLoading } = useSelector((state) => state.auth);

    useEffect(() => {
        // التحقق من وجود بيانات مؤقتة (يعني المستخدم لم يقرر بعد)
        const tempData = sessionStorage.getItem("adminkhadamat_temp");
        
        // لا نعيد التوجيه إذا:
        // 1. هناك بيانات مؤقتة (المستخدم لم يقرر بعد)
        // 2. هناك مودال مفتوح
        // 3. المستخدم يحتاج 2FA
        if (tempData || show2FAPromptModal || showQRModal || showVerifyModal || need2FA) {
            return; // لا نعيد التوجيه
        }
        
        // فقط إذا كان المستخدم مسجل دخول بالفعل وليس هناك مودال أو بيانات مؤقتة
        if (user && !need2FA) {
            window.location.href = '/';
        }
    }, [user, need2FA, show2FAPromptModal, showQRModal, showVerifyModal]);

    // handleEnable2FA - يُستخدم في حالتين:
    // 1. عند تسجيل الدخول إذا كان 2FA مفعل بالفعل (للحصول على QR للـ scan)
    // 2. عند اختيار "تفعيل 2FA" من المودال (للمستخدمين الذين ليس لديهم 2FA)
    const handleEnable2FA = useCallback(async () => {
        try {
            setLoading(true);
            const result = await dispatch(enable2FA()).unwrap();
            if (result.data?.qr_svg && result.data?.secret) {
                // إذا كان المودال مفتوح (يعني المستخدم اختار تفعيل من المودال)
                if (show2FAPromptModal) {
                    setShow2FAPromptModal(false);
                }
                // عرض QR modal
                setShowQRModal(true);
            }
        } catch (err) {
            showErrorToast(err.message || 'فشل في تفعيل 2FA');
        } finally {
            setLoading(false);
        }
    }, [dispatch, show2FAPromptModal]);

    const handleContinueWithout2FA = () => {
        setShow2FAPromptModal(false);
        // نقل البيانات من temp إلى sessionStorage النهائي
        const adminData = sessionStorage.getItem("adminkhadamat_temp");
        if (adminData) {
            try {
                const parsed = JSON.parse(adminData);
                // حفظ في sessionStorage النهائي
                sessionStorage.setItem("adminkhadamat", adminData);
                sessionStorage.removeItem("adminkhadamat_temp");
                
                // Save roles and permissions
                if (parsed.data?.roles) {
                    dispatch(setUserRoles({ roles: parsed.data.roles }));
                }
                
                // حفظ في Redux state - تحديث state.user
                dispatch({ 
                    type: 'auth/loginUser/fulfilled', 
                    payload: parsed 
                });
                
                showSuccessToast('تم تسجيل الدخول بنجاح');
                setTimeout(() => {
                    window.location.href = '/';
                }, 1500);
            } catch (err) {
                showErrorToast('حدث خطأ أثناء تسجيل الدخول');
            }
        } else {
            showErrorToast('لم يتم العثور على بيانات الجلسة');
        }
    };

    useEffect(() => {
        if (need2FA && twoFAEmail) {
            // إذا كان يحتاج 2FA (مفعل بالفعل)، نعرض verify modal مباشرة
            // لا نطلب enable-2fa لأنه مفعل بالفعل
            setShowVerifyModal(true);
        }
    }, [need2FA, twoFAEmail]);

    // useEffect إضافي لفتح QR modal عند تحديث twoFAData (عند اختيار تفعيل 2FA)
    useEffect(() => {
        if (twoFAData?.qr_svg && twoFAData?.secret) {
            setShowQRModal(true);
        }
    }, [twoFAData]);


    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        if (!email || !password) {
            setShake(true);
            showErrorToast('يجب إدخال البريد الإلكتروني وكلمة المرور');
            setTimeout(() => setShake(false), 500);
            setLoading(false);
            return;
        }

        try {
            let webFcmToken = '';
            try {
                webFcmToken = await getWebFcmToken();
            } catch {
                webFcmToken = '';
            }
            const result = await dispatch(
                loginUser({
                    email,
                    password,
                    web_fcm_token: webFcmToken || '',
                })
            ).unwrap();
            
            // إذا كان يحتاج 2FA (مفعل بالفعل) - نعرض QR للـ scan مباشرة
            if (result.need_2fa || result.data?.need_2fa) {
                // سيتم التعامل معها في useEffect لعرض verify modal أو QR modal
                return;
            }
            
            // إذا تم تسجيل الدخول بنجاح بدون 2FA (يعني غير مفعل)
            // سيتم توجيهه للصفحة الرئيسية وإظهار مودال التفعيل من داخل الصفحة الرئيسية
            if (result.status === 200 || result.statusCode === 200) {
                if (result.data?.token) {
                    // نحفظ الجلسة النهائية ونرفع علم لعرض المودال داخل الصفحة الرئيسية
                    sessionStorage.setItem("adminkhadamat", JSON.stringify(result));
                    sessionStorage.removeItem("adminkhadamat_temp");
                    sessionStorage.setItem("show2FAPromptHome", "1");
                    // حفظ البريد لاستخدامه في verify-2fa لاحقاً
                    if (email) {
                        sessionStorage.setItem('adminEmail', email);
                    }
                    // حفظ الأدوار إن وُجدت
                    if (result.data?.roles) {
                        dispatch(setUserRoles({ roles: result.data.roles }));
                    }
                    // تحديث حالة المستخدم في Redux
                    dispatch({ type: 'auth/loginUser/fulfilled', payload: result });
                    // التوجيه للصفحة الرئيسية
                    window.location.href = '/';
                    return;
                }
            }
            
            // Save roles and permissions
            if (result.data?.roles) {
                dispatch(setUserRoles({ roles: result.data.roles }));
            }
            
            showSuccessToast('تم تسجيل الدخول بنجاح');
            setTimeout(() => {
                window.location.href = '/';
            }, 1500);
        } catch (err) {
            setShake(true);
            showErrorToast(err.message || 'فشل تسجيل الدخول');
            setTimeout(() => setShake(false), 500);
        } finally {
            setLoading(false);
        }
    };

    const handleVerify2FA = async (e) => {
        e.preventDefault();
        if (!verificationCode.trim()) {
            showErrorToast('يرجى إدخال كود التحقق');
            return;
        }

        try {
            setLoading(true);
            const emailToUse = twoFAEmail || email;
            let webFcmToken = '';
            try {
                webFcmToken = await getWebFcmToken();
            } catch {
                webFcmToken = '';
            }
            const result = await dispatch(
                verify2FA({
                    email: emailToUse,
                    code: verificationCode.trim(),
                    web_fcm_token: webFcmToken || '',
                })
            ).unwrap();
            
            // البيانات محفوظة بالفعل في Redux reducer
            
            // Save roles and permissions - التحقق من البنية الجديدة
            if (result.data?.admin && Array.isArray(result.data.admin) && result.data.admin.length > 0) {
                const roles = result.data.admin.map(admin => ({
                    role_name_en: admin.role_name_en,
                    permissions: admin.permissions || []
                }));
                dispatch(setUserRoles({ roles }));
            } else if (result.data?.roles) {
                dispatch(setUserRoles({ roles: result.data.roles }));
            }
            
            showSuccessToast('تم التحقق بنجاح');
            setShowVerifyModal(false);
            setShowQRModal(false);
            setVerificationCode('');
            
            setTimeout(() => {
                window.location.href = '/';
            }, 1500);
        } catch (err) {
            setShake(true);
            showErrorToast(err.message || 'كود التحقق غير صحيح');
            setTimeout(() => setShake(false), 500);
        } finally {
            setLoading(false);
        }
    };

    const LoadingSpinner = () => (
        <div className="spinner-border text-light" role="status" style={{ width: '1.5rem', height: '1.5rem' }}>
            <span className="visually-hidden">Loading...</span>
        </div>
    );

    return (
        <div className="container-fluid d-flex p-0 auth-page"
            style={{
                minHeight: '100vh',
                background: 'white'
            }}>
            
            
            {/* النموذج في المنتصف */}
            <div className="col-12 d-flex justify-content-center align-items-center px-4 my-5">
                <motion.div
                    initial={{ opacity: 0, x: 50 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.8 }}
                    className={`card shadow-lg ${shake ? 'shake-animation' : ''}`}
                    style={{
                        width: '100%',
                        maxWidth: '450px',
                        border: 'none',
                        borderRadius: '20px',
                        overflow: 'hidden',
                        backgroundColor: 'white'
                    }}>

                    <div className="card-body p-5">
                        {/* اللوجو */}
                        <motion.div
                            initial={{ scale: 0.9 }}
                            animate={{ scale: 1 }}
                            transition={{ delay: 0.2, type: 'spring' }}
                            className="text-center mb-4">
                            <img 
                                src="/logokhadamat.png" 
                                alt="Khadamat Logo" 
                                style={{
                                    height: '80px',
                                    width: 'auto'
                                }}
                            />
                        </motion.div>

                        {/* عنوان مرحباً بعودتك */}
                        <motion.h3
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.4 }}
                            className="text-center mb-3 fw-bold"
                            style={{ color: '#333', fontSize: '18px' }}>
                            مرحباً بعودتك - مدير النظام
                        </motion.h3>

                        {/* نص يرجي تسجيل الدخول */}
                        <motion.p
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.5 }}
                            className="text-center mb-4"
                            style={{ 
                                color: '#184194',
                                fontSize: '1.1rem',
                                fontWeight: '500'
                            }}>
                            يرجي تسجيل الدخول
                        </motion.p>

                        <form onSubmit={handleSubmit}>
                            {/* حقل البريد الإلكتروني */}
                            <motion.div
                                initial={{ x: -20, opacity: 0 }}
                                animate={{ x: 0, opacity: 1 }}
                                transition={{ delay: 0.6 }}
                                className="mb-4">
                                <label htmlFor="email" className="form-label fw-medium" style={{ color: '#333' }}>
                                    البريد الإلكتروني
                                </label>
                                <div className="input-group">
                                    <span className="input-group-text bg-light">
                                        <FaEnvelope className="text-secondary" />
                                    </span>
                                    <input
                                        type="email"
                                        className="form-control"
                                        id="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        placeholder="example@example.com"
                                        required
                                        style={{ 
                                            borderLeft: 'none',
                                            borderRadius: '0 8px 8px 0'
                                        }}
                                    />
                                </div>
                            </motion.div>

                            {/* حقل كلمة المرور */}
                            <motion.div
                                initial={{ x: -20, opacity: 0 }}
                                animate={{ x: 0, opacity: 1 }}
                                transition={{ delay: 0.7 }}
                                className="mb-4">
                                <label htmlFor="password" className="form-label fw-medium" style={{ color: '#333' }}>
                                    كلمة المرور
                                </label>
                                <div className="input-group password-input">
                                    <input
                                        type={showPassword ? "text" : "password"}
                                        className="form-control"
                                        id="password"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        placeholder="أدخل كلمة المرور"
                                        required
                                        style={{ 
                                            borderRadius: '8px 0 0 8px'
                                        }}
                                        autoComplete="new-password"
                                    />
                                    <button
                                        type="button"
                                        className="input-group-text bg-light"
                                        onClick={() => setShowPassword(!showPassword)}
                                        style={{ cursor: 'pointer', borderRadius: '0 8px 8px 0' }}>
                                        {showPassword ? <FaEyeSlash className="text-secondary" /> : <FaEye className="text-secondary" />}
                                    </button>
                                </div>
                            </motion.div>


                            {/* زر تسجيل الدخول */}
                            <motion.button
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                                type="submit"
                                className="btn w-100 text-white py-3 fw-medium"
                                style={{
                                    backgroundColor: '#184194',
                                    border: 'none',
                                    borderRadius: '10px',
                                    fontSize: '1.1rem',
                                    marginTop: '1rem'
                                }}
                                disabled={loading}>
                                {loading ? <LoadingSpinner /> : 'تسجيل الدخول'}
                            </motion.button>
                        </form>

                        {/* روابط للصفحات الأخرى */}
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.8 }}
                            className="mt-4 text-center">
                          
                        </motion.div>
                    </div>
                </motion.div>
            </div>

            {/* QR Code Modal */}
            {showQRModal && twoFAData && (
                <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 9999 }}>
                    <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: '500px' }}>
                        <div className="modal-content" style={{ 
                            borderRadius: '20px', 
                            border: 'none', 
                            boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
                            overflow: 'hidden'
                        }}>
                            <div className="modal-header" style={{ 
                                background: `linear-gradient(135deg, var(--main-color) 0%, #764ba2 100%)`, 
                                color: 'white',
                                border: 'none',
                                padding: '25px 30px',
                                borderRadius: '20px 20px 0 0'
                            }}>
                                <h5 className="modal-title mb-0 d-flex align-items-center" style={{ fontSize: '1.25rem', fontWeight: '600' }}>
                                    <FaQrcode className="mx-2" style={{ fontSize: '1.5rem' }} />
                                    تفعيل المصادقة الثنائية
                                </h5>
                                <button
                                    type="button"
                                    className="btn-close btn-close-white"
                                    onClick={() => {
                                        setShowQRModal(false);
                                        dispatch(clear2FA());
                                        setEmail('');
                                        setPassword('');
                                    }}
                                    style={{ fontSize: '1.2rem', opacity: '0.9' }}
                                ></button>
                            </div>
                            <div className="modal-body text-center p-5" style={{ background: '#ffffff' }}>
                                <div style={{ marginBottom: '25px' }}>
                                    <h4 style={{ 
                                        color: '#1a1a1a', 
                                        marginBottom: '10px', 
                                        fontSize: '1.3rem',
                                        fontWeight: '600'
                                    }}>
                                        امسح رمز QR
                                    </h4>
                                    <p style={{ 
                                        color: '#666', 
                                        fontSize: '0.95rem',
                                        marginBottom: '0'
                                    }}>
                                        استخدم تطبيق Google Authenticator لمسح الرمز
                                    </p>
                                </div>
                                
                                <div 
                                    id="qrBox"
                                    className="qr-box mb-4" 
                                    style={{ 
                                        background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)', 
                                        border: '3px solid #e0e0e0', 
                                        borderRadius: '16px', 
                                        padding: '25px',
                                        boxShadow: '0 4px 15px rgba(0,0,0,0.08)',
                                        display: 'inline-block',
                                        maxWidth: '100%',
                                        textAlign: 'center',
                                        position: 'relative'
                                    }}
                                >
                                    {twoFAData?.qr_svg ? (
                                        <div
                                            style={{
                                                width: '280px',
                                                height: '280px',
                                                margin: '0 auto',
                                                position: 'relative',
                                                background: 'white',
                                                borderRadius: '12px',
                                                padding: '15px',
                                                boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
                                            }}
                                        >
                                            <div
                                                style={{
                                                    width: '100%',
                                                    height: '100%',
                                                    position: 'relative',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center'
                                                }}
                                                dangerouslySetInnerHTML={{ __html: twoFAData.qr_svg }}
                                            />
                                        </div>
                                    ) : (
                                        <div className="text-muted py-5" style={{ fontSize: '1rem' }}>
                                            <div className="spinner-border text-primary mb-2" role="status" style={{ color: 'var(--main-color)' }}>
                                                <span className="visually-hidden">Loading...</span>
                                            </div>
                                            <div>جاري تحميل رمز QR...</div>
                                        </div>
                                    )}
                                </div>
                                
                                <div style={{ 
                                    background: '#f8f9fa',
                                    borderRadius: '12px',
                                    padding: '20px',
                                    marginBottom: '25px',
                                    border: '1px solid #e9ecef'
                                }}>
                                    <p style={{ 
                                        color: '#495057', 
                                        marginBottom: '12px', 
                                        fontSize: '0.95rem',
                                        fontWeight: '500'
                                    }}>
                                        أو أدخل الكود يدوياً:
                                    </p>
                                    <div 
                                        className="secret" 
                                        style={{ 
                                            background: 'white', 
                                            padding: '12px 20px', 
                                            borderRadius: '8px',
                                            fontFamily: 'monospace',
                                            fontSize: '18px',
                                            fontWeight: '600',
                                            color: 'var(--main-color)',
                                            letterSpacing: '2px',
                                            display: 'inline-block',
                                            border: '2px solid #dee2e6',
                                            boxShadow: '0 2px 5px rgba(0,0,0,0.05)'
                                        }}
                                    >
                                        {twoFAData.secret}
                                    </div>
                                </div>
                                
                                <div className="alert alert-light border-start border-primary border-4" style={{
                                    background: '#e7f3ff',
                                    borderRadius: '12px',
                                    padding: '15px 20px',
                                    marginBottom: '0',
                                    borderLeft: '4px solid var(--main-color) !important'
                                }}>
                                    <div className="d-flex align-items-center justify-content-center">
                                        <FaMobileAlt className="me-2" style={{ fontSize: '1.3rem', color: 'var(--main-color)' }} />
                                        <div className="text-start">
                                            <strong style={{ color: '#1a1a1a', fontSize: '0.95rem' }}>
                                                قم بتحميل تطبيق Google Authenticator
                                            </strong>
                                            <br />
                                            <small style={{ color: '#666', fontSize: '0.85rem' }}>
                                                متوفر على App Store و Google Play
                                            </small>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer" style={{ 
                                border: 'none',
                                padding: '20px 30px 30px',
                                background: '#ffffff',
                                borderRadius: '0 0 20px 20px'
                            }}>
                                <button
                                    type="button"
                                    className="btn"
                                    onClick={() => {
                                        setShowQRModal(false);
                                        dispatch(clear2FA());
                                        setEmail('');
                                        setPassword('');
                                    }}
                                    style={{ 
                                        background: '#f8f9fa',
                                        color: '#495057',
                                        border: '1px solid #dee2e6',
                                        borderRadius: '10px',
                                        padding: '10px 25px',
                                        fontWeight: '500',
                                        marginLeft: '10px'
                                    }}
                                    onMouseEnter={(e) => e.target.style.background = '#e9ecef'}
                                    onMouseLeave={(e) => e.target.style.background = '#f8f9fa'}
                                >
                                    إلغاء
                                </button>
                                <button
                                    type="button"
                                    className="btn"
                                    onClick={() => {
                                        setShowQRModal(false);
                                        // الانتقال لصفحة verify-2fa
                                        navigate(`/admin/verify-2fa?email=${encodeURIComponent(twoFAEmail || email)}`);
                                    }}
                                    style={{ 
                                        backgroundColor: 'var(--main-color)',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '10px',
                                        padding: '10px 30px',
                                        fontWeight: '500',
                                        boxShadow: '0 4px 10px rgba(24, 65, 148, 0.3)'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.target.style.backgroundColor = 'rgba(24, 65, 148, 0.85)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.target.style.backgroundColor = 'var(--main-color)';
                                    }}
                                >
                                    متابعة: إدخال الكود
                                </button>
                            </div>
                        </div>
                    </div>
                    <style>{`
                        #qrBox svg rect[fill="#fefefe"] {
                            fill: #ffffff !important;
                        }
                        #qrBox svg path {
                            fill: #000000 !important;
                        }
                    `}</style>
                </div>
            )}

            {/* Verify 2FA Modal */}
            {showVerifyModal && (need2FA || twoFAEmail) && (
                <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 9999 }}>
                    <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: '450px' }}>
                        <div className="modal-content" style={{ 
                            borderRadius: '20px', 
                            border: 'none', 
                            boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
                            overflow: 'hidden'
                        }}>
                            <div className="modal-header" style={{ 
                                background: `linear-gradient(135deg, var(--main-color) 0%, #764ba2 100%)`, 
                                color: 'white',
                                border: 'none',
                                padding: '25px 30px',
                                borderRadius: '20px 20px 0 0'
                            }}>
                                <h5 className="modal-title mb-0 d-flex align-items-center" style={{ fontSize: '1.25rem', fontWeight: '600' }}>
                                    <FaCheckCircle className="mx-2" style={{ fontSize: '1.5rem' }} />
                                    التحقق من المصادقة الثنائية
                                </h5>
                                <button
                                    type="button"
                                    className="btn-close btn-close-white"
                                    onClick={() => {
                                        setShowVerifyModal(false);
                                        dispatch(clear2FA());
                                        setEmail('');
                                        setPassword('');
                                        setVerificationCode('');
                                    }}
                                    style={{ fontSize: '1.2rem', opacity: '0.9' }}
                                ></button>
                            </div>
                            <div className="modal-body p-5">
                                <form onSubmit={handleVerify2FA}>
                                    <div className="mb-4">
                                        <label className="form-label fw-medium" style={{ color: '#333', marginBottom: '12px', display: 'block' }}>
                                            كود التحقق من Google Authenticator
                                        </label>
                                        <input
                                            type="text"
                                            className="form-control verify-2fa-input"
                                            value={verificationCode}
                                            onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                            placeholder="أدخل الكود المكون من 6 أرقام"
                                            maxLength="6"
                                            required
                                            autoFocus
                                            style={{ 
                                                fontSize: '28px',
                                                textAlign: 'center',
                                                letterSpacing: '10px',
                                                fontFamily: 'monospace',
                                                fontWeight: 'bold',
                                                border: '2px solid #dee2e6',
                                                borderRadius: '12px',
                                                padding: '15px',
                                                background: '#f8f9fa'
                                            }}
                                            onFocus={(e) => {
                                                e.target.style.borderColor = 'var(--main-color)';
                                                e.target.style.background = 'white';
                                                e.target.style.boxShadow = '0 0 0 0.2rem rgba(24, 65, 148, 0.1)';
                                            }}
                                            onBlur={(e) => {
                                                e.target.style.borderColor = '#dee2e6';
                                                e.target.style.background = '#f8f9fa';
                                                e.target.style.boxShadow = 'none';
                                            }}
                                        />
                                        <small className="text-muted d-block mt-2" style={{ fontSize: '0.85rem' }}>
                                            أدخل الكود من تطبيق Google Authenticator
                                        </small>
                                    </div>
                                    <div className="d-grid gap-2">
                                        <button
                                            type="submit"
                                            className="btn"
                                            disabled={loading || verificationCode.length !== 6}
                                            style={{ 
                                                backgroundColor: 'var(--main-color)',
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: '10px',
                                                padding: '12px',
                                                fontWeight: '500',
                                                fontSize: '1rem',
                                                boxShadow: '0 4px 10px rgba(24, 65, 148, 0.3)'
                                            }}
                                            onMouseEnter={(e) => {
                                                if (!e.target.disabled) {
                                                    e.target.style.backgroundColor = 'rgba(24, 65, 148, 0.85)';
                                                }
                                            }}
                                            onMouseLeave={(e) => {
                                                e.target.style.backgroundColor = 'var(--main-color)';
                                            }}
                                        >
                                            {loading ? <LoadingSpinner /> : 'تحقق وتسجيل الدخول'}
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-link"
                                            onClick={() => {
                                                setShowVerifyModal(false);
                                                dispatch(clear2FA());
                                                setEmail('');
                                                setPassword('');
                                                setVerificationCode('');
                                            }}
                                            style={{
                                                color: '#6c757d',
                                                textDecoration: 'none',
                                                padding: '8px',
                                                marginTop: '5px'
                                            }}
                                            onMouseEnter={(e) => e.target.style.color = 'var(--main-color)'}
                                            onMouseLeave={(e) => e.target.style.color = '#6c757d'}
                                        >
                                            إلغاء
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* 2FA Prompt Modal - عند تسجيل الدخول بدون 2FA */}
            {show2FAPromptModal && (
                <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 9999 }}>
                    <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: '450px' }}>
                        <div className="modal-content" style={{ 
                            borderRadius: '20px', 
                            border: 'none', 
                            boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
                            overflow: 'hidden'
                        }}>
                            <div className="modal-header" style={{ 
                                background: `linear-gradient(135deg, var(--main-color) 0%, #764ba2 100%)`, 
                                color: 'white',
                                border: 'none',
                                padding: '25px 30px',
                                borderRadius: '20px 20px 0 0'
                            }}>
                                <h5 className="modal-title mb-0 d-flex align-items-center" style={{ fontSize: '1.25rem', fontWeight: '600' }}>
                                    <FaMobileAlt className="mx-2" style={{ fontSize: '1.5rem' }} />
                                    تفعيل المصادقة الثنائية
                                </h5>
                                <button
                                    type="button"
                                    className="btn-close btn-close-white"
                                    onClick={handleContinueWithout2FA}
                                    style={{ fontSize: '1.2rem', opacity: '0.9' }}
                                ></button>
                            </div>
                            <div className="modal-body p-5 text-center" style={{ background: '#ffffff' }}>
                                <div className="mb-4" style={{ 
                                    width: '100px',
                                    height: '100px',
                                    margin: '0 auto 25px',
                                    background: 'linear-gradient(135deg, var(--main-color) 0%, #764ba2 100%)',
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    boxShadow: '0 8px 25px rgba(24, 65, 148, 0.25)'
                                }}>
                                    <FaMobileAlt style={{ fontSize: '2.5rem', color: 'white' }} />
                                </div>
                                <h4 className="mb-3" style={{ 
                                    color: '#1a1a1a', 
                                    fontSize: '1.3rem',
                                    fontWeight: '600'
                                }}>
                                    هل تريد تفعيل المصادقة الثنائية؟
                                </h4>
                                <p className="text-muted" style={{ 
                                    color: '#666',
                                    fontSize: '0.95rem',
                                    lineHeight: '1.6',
                                    marginBottom: '0'
                                }}>
                                    تفعيل 2FA يضيف طبقة حماية إضافية لحسابك باستخدام تطبيق Google Authenticator
                                </p>
                            </div>
                            <div className="modal-footer justify-content-center" style={{ 
                                border: 'none',
                                padding: '20px 30px 30px',
                                background: '#ffffff',
                                borderRadius: '0 0 20px 20px'
                            }}>
                                <button
                                    type="button"
                                    className="btn"
                                    onClick={handleContinueWithout2FA}
                                    style={{ 
                                        background: '#f8f9fa',
                                        color: '#495057',
                                        border: '1px solid #dee2e6',
                                        borderRadius: '10px',
                                        padding: '10px 25px',
                                        fontWeight: '500',
                                        marginLeft: '10px'
                                    }}
                                    onMouseEnter={(e) => e.target.style.background = '#e9ecef'}
                                    onMouseLeave={(e) => e.target.style.background = '#f8f9fa'}
                                >
                                    متابعة بدون تفعيل
                                </button>
                                <button
                                    type="button"
                                    className="btn"
                                    onClick={handleEnable2FA}
                                    disabled={loading}
                                    style={{ 
                                        backgroundColor: 'var(--main-color)',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '10px',
                                        padding: '10px 30px',
                                        fontWeight: '500',
                                        boxShadow: '0 4px 10px rgba(24, 65, 148, 0.3)'
                                    }}
                                    onMouseEnter={(e) => {
                                        if (!e.target.disabled) {
                                            e.target.style.backgroundColor = 'rgba(24, 65, 148, 0.85)';
                                        }
                                    }}
                                    onMouseLeave={(e) => {
                                        e.target.style.backgroundColor = 'var(--main-color)';
                                    }}
                                >
                                    {loading ? <LoadingSpinner /> : 'تفعيل 2FA'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* الانيميشن الدائري */}
            <div className="position-fixed top-0 end-0 m-4">
                <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ repeat: Infinity, duration: 20, ease: "linear" }}
                    style={{
                        width: '150px',
                        height: '150px',
                        border: '2px dashed white',
                        borderRadius: '50%',
                        position: 'absolute',
                        top: '-50px',
                        right: '-50px'
                    }}
                />
            </div>
            <style>{`
                .verify-2fa-input::placeholder { font-size: 14px; letter-spacing: normal; }
                .verify-2fa-input::-webkit-input-placeholder { font-size: 14px; letter-spacing: normal; }
                .verify-2fa-input::-moz-placeholder { font-size: 14px; letter-spacing: normal; }
                .verify-2fa-input:-ms-input-placeholder { font-size: 14px; letter-spacing: normal; }
                .verify-2fa-input:-moz-placeholder { font-size: 14px; letter-spacing: normal; }
                #qrBox svg {
                    display: block !important;
                    width: 250px !important;
                    height: 250px !important;
                    max-width: 100% !important;
                    max-height: 100% !important;
                }
                #qrBox svg rect {
                    fill: #fefefe !important;
                }
                #qrBox svg path {
                    fill: #000000 !important;
                }
            `}</style>
        </div>
    );
};

export default AdminLoginPage;
